import { Component } from '@theme/component';

/** @type {Promise<void> | null} */
let googleMapsLoadPromise = null;

/**
 * @param {string} apiKey
 * @returns {Promise<void>}
 */
function loadGoogleMaps(apiKey) {
  if (window.google?.maps) {
    return Promise.resolve();
  }

  if (googleMapsLoadPromise) {
    return googleMapsLoadPromise;
  }

  googleMapsLoadPromise = new Promise((resolve, reject) => {
    const callbackName = '__mapTabsGoogleMapsInit';

    /** @type {Window & typeof globalThis & Record<string, () => void>} */
    const globalWindow = window;

    globalWindow[callbackName] = () => {
      delete globalWindow[callbackName];
      resolve();
    };

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&callback=${callbackName}`;
    script.async = true;
    script.onerror = () => {
      googleMapsLoadPromise = null;
      reject(new Error('Google Maps failed to load'));
    };
    document.head.appendChild(script);
  });

  return googleMapsLoadPromise;
}

/**
 * @typedef {Object} MapTabsMapRefs
 * @property {HTMLDivElement} canvas - Map container element
 */

/**
 * @extends {Component<MapTabsMapRefs>}
 */
class MapTabsMap extends Component {
  /** @type {google.maps.Map | null} */
  #map = null;

  /** @type {google.maps.Marker | null} */
  #marker = null;

  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** @type {boolean} */
  #initialized = false;

  connectedCallback() {
    super.connectedCallback();
    this.#observeVisibility();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#marker = null;
    this.#map = null;
    this.#initialized = false;
  }

  #observeVisibility() {
    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];

        if (!entry?.isIntersecting || this.#initialized) {
          return;
        }

        this.#initialized = true;
        this.#intersectionObserver?.disconnect();
        this.#intersectionObserver = null;
        this.#initMap();
      },
      { rootMargin: '200px' },
    );

    this.#intersectionObserver.observe(this);
  }

  async #initMap() {
    const apiKey = this.dataset.apiKey?.trim();
    const address = this.dataset.address?.trim();
    const lat = Number.parseFloat(this.dataset.lat ?? '');
    const lng = Number.parseFloat(this.dataset.lng ?? '');
    const zoom = Number.parseInt(this.dataset.zoom ?? '15', 10);
    const mapLabel = this.dataset.mapLabel?.trim();
    const canvas = this.refs.canvas;

    if (!apiKey || !canvas) {
      return;
    }

    try {
      await loadGoogleMaps(apiKey);
    } catch {
      this.classList.add('map-tabs-map--error');
      return;
    }

    if (!window.google?.maps) {
      return;
    }

    const hasCoordinates = Number.isFinite(lat) && Number.isFinite(lng);

    if (hasCoordinates) {
      this.#renderMap({ lat, lng }, zoom, mapLabel || address);
      return;
    }

    if (!address) {
      return;
    }

    const geocoder = new google.maps.Geocoder();

    geocoder.geocode({ address }, (results, status) => {
      if (status !== 'OK' || !results?.[0]?.geometry?.location) {
        this.classList.add('map-tabs-map--error');
        return;
      }

      const location = results[0].geometry.location;
      this.#renderMap({ lat: location.lat(), lng: location.lng() }, zoom, mapLabel || address);
    });
  }

  /**
   * @param {{ lat: number, lng: number }} position
   * @param {number} zoom
   * @param {string} [label]
   */
  #renderMap(position, zoom, label) {
    const canvas = this.refs.canvas;

    if (!canvas || !window.google?.maps) {
      return;
    }

    const center = new google.maps.LatLng(position.lat, position.lng);

    this.#map = new google.maps.Map(canvas, {
      center,
      zoom,
      mapTypeControl: false,
      streetViewControl: true,
      fullscreenControl: true,
    });

    this.#marker = new google.maps.Marker({
      map: this.#map,
      position: center,
      title: label || undefined,
    });

    this.classList.add('map-tabs-map--ready');
  }
}

if (!customElements.get('map-tabs-map')) {
  customElements.define('map-tabs-map', MapTabsMap);
}
